import { Injectable } from '@angular/core';
import { ConsumptionQueryModel } from '@app/@shared/models';
import { DateModeType, SmartComparison } from '@core/enums/consumption-filter.enum';
import { TranslocoService } from '@ngneat/transloco';
import { addDays, differenceInCalendarDays, getISOWeek, getYear, isEqual, isSameDay, isSameMonth, isSameWeek, isSameYear, setISOWeek, setISOWeekYear, setMonth, startOfISOWeek, startOfMonth, startOfYear, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { DisabledTimeFn } from 'ng-zorro-antd/date-picker';
@Injectable({
  providedIn: 'root'
})
export class CommonDateEvents {
  constructor(private translocoService: TranslocoService) { }

  /**
   * dates ranges
   * @param start
   * @param end
   */
  datesRange(start: number, end: number): number[] {
    const result: number[] = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  }

  /**
   * disable date times
   */
  disabledDateTime: DisabledTimeFn = (date: any) => {
    return {
      nzDisabledHours: () => this.datesRange(new Date().getHours() + 1, 24),
      nzDisabledMinutes: () => this.datesRange(new Date().getMinutes() + 1, 60),
      nzDisabledSeconds: () => this.datesRange(new Date().getSeconds(), 60)
    };
  }

  /**
   * geçerli tarih öncesi ve sonrasını kilitleme
   * @param opts
   */
  lockingBeforeAndAfter = (opts: { current: Date, dateLeft: Date, dateRight?: Date }): boolean => {
    return differenceInCalendarDays(opts.current, opts.dateLeft) > 0 || differenceInCalendarDays(opts.current, opts.dateRight) < 0;
  }

  /**
   * tarih formatlarını UTC'ye çeviriyoruz.
   * @param dateStr
   */
  dateToUTC = (dateStr: string | Date | number): Date => {
    const date = new Date(dateStr);
    const nowUtc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    return new Date(nowUtc);
  }

  /**
   * akıllı seçim işlemleri
   * @param date
   * @param mode
   */
  smartComparison = (date: Date | string, mode: number): Date | string => {
    const currentDate = new Date(date);

    switch (mode) {

      case SmartComparison.PreviousYearSameDay:  // Önceki Sene Aynı Gün
      case SmartComparison.PreviousYearSameMonth: // Önceki Sene Aynı Ay
      case SmartComparison.PreviousYearSameWeek: // Önceki Sene Aynı Hafta
      case SmartComparison.PreviousWeekOfYear: // Sene (Haftalık) Önceki Sene
        const subYear = subYears(currentDate, 1);
        return setISOWeekYear(currentDate, getYear(subYear));

      case SmartComparison.ADayAgo: // Birer Gün Öncesi
      case SmartComparison.AMonthAgo: // Birer Ay Öncesi
      case SmartComparison.AWeekAgoYear: // Sene (Haftalık) Birer Hafta Öncesi
        return currentDate;

      // Önceki Hafta Aynı Gün
      case SmartComparison.PreviousWeekSameDay:
        return subDays(currentDate, 7);
      // Önceki Ay
      case SmartComparison.PreviousMonth:
        return subMonths(currentDate, 1);

      // Önceki Sene
      case SmartComparison.PreviousYear:
        return subYears(currentDate, 1);

      // Birer Hafta Öncesi
      case SmartComparison.AWeekAgo:
        return subWeeks(currentDate, 1);

      default:
        //
        break;
    }
  }

  /**
   * gönderilen tarihlerin eşit olup olmadığına bakıyoruz.
   * @param dateLeft
   * @param dateRight
   */
  isEqualDate(dateLeft: Date | string, dateRight: Date | string): boolean {
    return isEqual(new Date(dateLeft), new Date(dateRight));
  }

  /**
   * seçile tarih moduna göre grafik keyi üretir.
   * @param dateMode
   */
  async createChartKeys(dateMode: number, maxNumber: number = 31): Promise<(string | number)[]> {
    const keys: Array<string | number> = [];

    switch (dateMode) {
      // günlere göre seçim yapılmışsa
      case DateModeType.Day:
        for (let i = 0; i <= 23; i++) {
          keys.push(('0' + i).slice(-2) + ':00');
        }
        return keys;

      // haftalara göre seçim yapılmuşsa
      case DateModeType.Week:
        return new Promise((r: any) => {
          this.translocoService.selectTranslate('COMMON_DATE_EVENTS.DAYS.LONG').subscribe((key: string[]) => {
            r(key);
          });
        });

      // aylara göre seçim yapılmışsa
      case DateModeType.Month:
        for (let i = 1; i <= maxNumber; i++) {
          keys.push(i);
        }
        return keys;

      // senelere göre haftalık seçim yapılmışsa
      case DateModeType.YearWeekly:
        return new Promise((r: any) => {
          this.translocoService.selectTranslate('COMMON_DATE_EVENTS.WEEK_SHORT').subscribe((key: string) => {
            for (let yw = 1; yw <= maxNumber; yw++) {
              keys.push((key + yw));
            }
            r(keys);
          });
        });

      // senelere göre aylık seçim yapılmuşsa
      case DateModeType.YearMonthly:
        return new Promise((r: any) => {
          this.translocoService.selectTranslate('COMMON_DATE_EVENTS.MONTHS').subscribe((key: string[]) => {
            r(key);
          });
        });

      default:
        //
        break;
    }
  }

  /**
   * Tarihleri lokal tarih formatlarına dönüşümünü sağlıyoruz.
   * @param date
   * @param dateFormat
   */
  toLocaleDateFn(date: any, dateFormat: any = {}): any {
    return new Date(date).toLocaleDateString(this.translocoService.getActiveLang(), dateFormat);
  }

  /**
   * apex chart özel tooltipte gösterilecek tarih biçimleri
   * @param dateMode
   * @param dates
   */
  apexChartCustomDateFormatter(opts: { dateMode: number, dates: any, index?: number, type?: string }): any {
    const defaultFormat = { year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' };
    const legend = opts.type === 'legend' ? true : false;

    switch (opts.dateMode) {
      // günlere göre seçim yapılmışsa
      case DateModeType.Day:
        opts.dates = [
          this.toLocaleDateFn(opts.dates[0], defaultFormat),
          this.toLocaleDateFn(opts.dates[1], defaultFormat)
        ];
        break;

      // haftalara göre seçim yapılmışsa
      case DateModeType.Week:
        const formatterWeek = (date: any) => {
          const startIofWeek = startOfISOWeek(new Date(date));
          const getWeeks = getISOWeek(startIofWeek);
          const week = this.toLocaleDateFn(addDays(startIofWeek, opts.index));

          return (legend)
            ? getYear(startIofWeek) + ' ' + this.translocoService.translate('COMMON_DATE_EVENTS.WEEK_LONG', { value: getWeeks }) // legend için formatlıyoruz
            : `${this.translocoService.translate('COMMON_DATE_EVENTS.DAYS.LONG')[opts.index]} (${this.translocoService.translate('COMMON_DATE_EVENTS.WEEK_SHORT')}${getWeeks}) ${week}`;
        };

        opts.dates = [
          formatterWeek(opts.dates[0]),
          formatterWeek(opts.dates[1])
        ];
        break;

      // aylara göre seçim yapılmışsa
      case DateModeType.Month:
        const formatterMonth = (date: any) => {
          const startIofMonth = startOfMonth(new Date(date));


          return (legend)
            ? this.toLocaleDateFn(startIofMonth, { year: 'numeric', month: 'long' }) // legend için formatlıyoruz
            : this.toLocaleDateFn(addDays(startIofMonth, opts.index), { year: '2-digit', month: '2-digit', day: '2-digit', weekday: 'short' });
        };
        opts.dates = [
          formatterMonth(opts.dates[0]),
          formatterMonth(opts.dates[1])
        ];
        break;

      // Sene (haftalara göre) seçim yapılmışsa
      case DateModeType.YearWeekly:
        const formatterYearWeek = (date: any) => {
          const setISOWeekInYear = setISOWeek(startOfYear(new Date(date)), (opts.index + 1));
          const getISOWeekNumber = getISOWeek(setISOWeekInYear);
          const getYearWeek = getYear(new Date(date));

          return legend
            ? getYearWeek  // legend için formatlıyoruz
            : getYearWeek + ' ' + this.translocoService.translate('COMMON_DATE_EVENTS.WEEK_LONG', { value: getISOWeekNumber });
        };
        opts.dates = [
          formatterYearWeek(opts.dates[0]),
          formatterYearWeek(opts.dates[1])
        ];
        break;

      // Sene (aylara göre) seçim yapılmışsa
      case DateModeType.YearMonthly:
        const formatterYearMonth = (date: any) => {
          const setMonthYear = setMonth(new Date(date), opts.index);
          return legend
            ? getYear(new Date(date))  // legend için formatlıyoruz
            : this.toLocaleDateFn(setMonthYear, { month: 'long', year: 'numeric' });
        };
        opts.dates = [
          formatterYearMonth(opts.dates[0]),
          formatterYearMonth(opts.dates[1])
        ];
        break;
      // yukarıda ki seçimlerin dışında seçim varsa default
      default:
        opts.dates = [
          this.toLocaleDateFn(opts.dates[0], defaultFormat),
          this.toLocaleDateFn(opts.dates[1], defaultFormat)
        ];
        break;
    }

    return opts.dates;
  }

  /**
   * iki tarihin eşit olup olmadığını kontrol ediyoruz.
   * @param apiQuery
   * @param subDate
   */
  checkDateIsEqual(apiQuery: ConsumptionQueryModel, subDate?: boolean): any {

    let equal = false;
    const dates = {
      startDate: new Date(apiQuery.startDate),
      refStartDate: new Date(apiQuery.refStartDate)
    };

    switch (apiQuery.dateMode) {
      case DateModeType.Day:
        equal = isSameDay(dates.startDate, dates.refStartDate);
        dates.startDate = subDays(dates.refStartDate, 1);
        break;

      case DateModeType.Week:
        equal = isSameWeek(dates.refStartDate, dates.startDate);
        dates.startDate = subWeeks(dates.refStartDate, 1);
        break;

      case DateModeType.Month:
        equal = isSameMonth(dates.refStartDate, dates.startDate);
        dates.startDate = subMonths(dates.refStartDate, 1);
        break;

      case DateModeType.YearWeekly:
      case DateModeType.YearMonthly:
        equal = isSameYear(dates.refStartDate, dates.startDate);
        dates.startDate = subYears(dates.refStartDate, 1);
        break;
    }

    if (equal || subDate) {
      return {
        startDate: dates.startDate,
        refStartDate: dates.refStartDate
      };
    }

  }
}
